/**
 * Permits Tab Component
 * Manages building, demolition, and other permits for a job
 */

import { useState } from 'react';
import { usePermits } from '../../hooks/usePermits';
import type { CreatePermitRequest, PermitType } from '../../types/job';

interface PermitsTabProps {
  jobId: string;
}

interface PermitFormData {
  permit_number: string;
  permit_type: PermitType;
  issuing_jurisdiction: string;
  issue_date: string;
  expiration_date: string;
}

const PERMIT_TYPES: { value: PermitType; label: string }[] = [
  { value: 'building', label: 'Building Permit' },
  { value: 'demolition', label: 'Demolition Permit' },
  { value: 'grading', label: 'Grading Permit' },
  { value: 'other', label: 'Other' },
];

export function PermitsTab({ jobId }: PermitsTabProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState<PermitFormData>({
    permit_number: '',
    permit_type: 'building',
    issuing_jurisdiction: '',
    issue_date: '',
    expiration_date: '',
  });

  const { permits, isLoading, error, createPermit, deletePermit } = usePermits(jobId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const permitData: CreatePermitRequest = {
      permit_number: formData.permit_number,
      permit_type: formData.permit_type,
      issuing_jurisdiction: formData.issuing_jurisdiction,
      issue_date: formData.issue_date ? new Date(formData.issue_date).getTime() / 1000 : undefined,
      expiration_date: formData.expiration_date
        ? new Date(formData.expiration_date).getTime() / 1000
        : undefined,
    };

    const result = await createPermit(permitData);
    if (result) {
      setFormData({
        permit_number: '',
        permit_type: 'building',
        issuing_jurisdiction: '',
        issue_date: '',
        expiration_date: '',
      });
      setDialogOpen(false);
    }
  };

  const handleDelete = async (permitId: string) => {
    if (window.confirm('Are you sure you want to delete this permit?')) {
      await deletePermit(permitId);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const isExpired = (expirationDate: number) => {
    return expirationDate * 1000 < Date.now();
  };

  if (isLoading) {
    return (
      <div className="text-center py-8 text-neutral-600">
        <div className="animate-pulse">Loading permits...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add Permit Button */}
      <div>
        <button onClick={() => setDialogOpen(true)} className="btn-primary">
          <svg
            className="inline-block w-4 h-4 mr-2 -ml-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add Permit
        </button>
      </div>

      {/* Permits List */}
      {permits.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-neutral-500">No permits added yet</p>
          <p className="text-sm text-neutral-400 mt-2">
            Add building, demolition, or grading permits to track compliance
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {permits.map((permit) => (
            <div key={permit.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-neutral-900">
                    {PERMIT_TYPES.find((t) => t.value === permit.permit_type)?.label}
                  </h3>
                  <p className="text-sm text-neutral-500">#{permit.permit_number}</p>
                </div>
                <button
                  onClick={() => handleDelete(permit.id)}
                  className="text-neutral-400 hover:text-red-600 transition-colors p-2 -mr-2"
                  aria-label="Delete permit"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-neutral-500 mb-1">Jurisdiction</div>
                  <div className="text-neutral-900 font-medium">{permit.issuing_jurisdiction}</div>
                </div>

                {permit.issue_date && (
                  <div>
                    <div className="text-neutral-500 mb-1">Issue Date</div>
                    <div className="text-neutral-900 font-medium">
                      {formatDate(permit.issue_date)}
                    </div>
                  </div>
                )}

                {permit.expiration_date && (
                  <div>
                    <div className="text-neutral-500 mb-1">Expires</div>
                    <div
                      className={`font-medium ${
                        isExpired(permit.expiration_date) ? 'text-red-600' : 'text-neutral-900'
                      }`}
                    >
                      {formatDate(permit.expiration_date)}
                      {isExpired(permit.expiration_date) && (
                        <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                          Expired
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Permit Dialog */}
      {dialogOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setDialogOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-neutral-900 mb-2">Add Permit</h2>
              <p className="text-sm text-neutral-600">Record a permit for this project</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="permit_type" className="block text-sm font-medium text-neutral-700 mb-1">
                  Permit Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="permit_type"
                  value={formData.permit_type}
                  onChange={(e) =>
                    setFormData({ ...formData, permit_type: e.target.value as PermitType })
                  }
                  className="input-field"
                  required
                >
                  {PERMIT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="permit_number" className="block text-sm font-medium text-neutral-700 mb-1">
                  Permit Number <span className="text-red-500">*</span>
                </label>
                <input
                  id="permit_number"
                  type="text"
                  value={formData.permit_number}
                  onChange={(e) => setFormData({ ...formData, permit_number: e.target.value })}
                  placeholder="123456"
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="issuing_jurisdiction"
                  className="block text-sm font-medium text-neutral-700 mb-1"
                >
                  Issuing Jurisdiction <span className="text-red-500">*</span>
                </label>
                <input
                  id="issuing_jurisdiction"
                  type="text"
                  value={formData.issuing_jurisdiction}
                  onChange={(e) =>
                    setFormData({ ...formData, issuing_jurisdiction: e.target.value })
                  }
                  placeholder="City of San Francisco"
                  className="input-field"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="issue_date" className="block text-sm font-medium text-neutral-700 mb-1">
                    Issue Date
                  </label>
                  <input
                    id="issue_date"
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                    className="input-field"
                  />
                </div>

                <div>
                  <label
                    htmlFor="expiration_date"
                    className="block text-sm font-medium text-neutral-700 mb-1"
                  >
                    Expiration Date
                  </label>
                  <input
                    id="expiration_date"
                    type="date"
                    value={formData.expiration_date}
                    onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                    className="input-field"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setDialogOpen(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">
                  Add Permit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
